<script lang="ts">
import { computed, defineComponent, PropType, provide, reactive, readonly, ref, unref, watch } from 'vue'
import debounce from 'lodash-es/debounce'
import { useForm } from 'vee-validate'
import { FormState, SubmitHandler } from './base-form'
import './patterns'

export default defineComponent({
  name: 'base-form',
  inheritAttrs: false,
  props: {
    initialValues: {
      type: [Object, Function],
      default: undefined,
    },
    initialErrors: {
      type: Object,
      default: undefined,
    },
    validateOnMount: {
      type: Boolean,
      default: false,
    },
    onSubmit: {
      type: Function as PropType<SubmitHandler>,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'submit',
    'change',
  ],
  setup(props, ctx) {
    const initTask = ref<Promise<void> | undefined>(undefined)
    const submitHandlers = ref<SubmitHandler[]>([])
    const isReady = ref<boolean>(false)

    const {
      errors,
      values,
      meta,
      isSubmitting,
      submitCount,
      validate,
      validateField,
      handleReset,
      resetForm,
      handleSubmit,
      submitForm,
      setErrors,
      setFieldError,
      setFieldValue,
      setValues,
      setFieldTouched,
      setTouched,
    } = useForm({
      initialValues: props.initialValues instanceof Function ? {} : props.initialValues,
      initialErrors: props.initialErrors,
      validateOnMount: props.validateOnMount,
    })

    function onReset() {
      handleReset()
    }

    async function ready() {
      return initTask.value
    }

    function patchErrors(errors: Record<string, string | string[]>) {
      const patchedErrors: Record<string, string> = {}

      Object.keys(errors).forEach(key => {
        const error = errors[key]

        patchedErrors[key] = error instanceof Array ? error[0] : error
      })

      setErrors(patchedErrors)
    }

    const isDisabled = computed(() => {
      return !isReady.value || props.disabled || isSubmitting.value
    })

    const state = reactive({
      meta,
      errors,
      values,
      isSubmitting,
      isReady,
      submitCount,
      disabled: isDisabled,
      validate,
      validateField,
      handleReset,
      submitForm,
      setErrors: patchErrors,
      setFieldError,
      setFieldValue,
      setValues,
      setFieldTouched,
      setTouched,
      resetForm,
      ready,
    })

    provide('form', readonly(state))

    const slotProps = computed(() => {
      const formMeta = unref(meta)

      return {
        meta: formMeta,
        setErrors: state.setErrors,
        isReady: unref(isReady),
        isSubmitting: unref(isSubmitting),
        values: state.values,
        errors: unref(errors),
        submitCount: unref(submitCount),
        disabled: unref(isDisabled),
      }
    })

    async function onFormSubmit(values: Record<string, string>) {
      for (const handler of submitHandlers.value) {
        try {
          const isSuccess = await handler(values, slotProps.value as unknown as  FormState)

          if (isSuccess === false) {
            return false
          }
        } catch (error) {
          console.error(error)
          return false
        }
      }

      if (props.onSubmit instanceof Function) {
        await props.onSubmit(values, slotProps.value)
      }
    }

    async function getInitialValues() {
      let values = props.initialValues

      if (props.initialValues instanceof Function) {
        isReady.value = false
        values = await props.initialValues()
      }

      if (values) {
        /*
         * Ensure that state is independent
         */
        setValues(values)
      }

      isReady.value = true
    }

    function updateFieldValue(name: string, value: string) {
      setFieldValue(name, value)
    }

    watch(() => props.initialValues, () => {
      initTask.value = getInitialValues()
    })

    watch(values, debounce((newValues) => {
      ctx.emit('change', newValues)
    }, 50))

    initTask.value = getInitialValues()

    return {
      submit: handleSubmit(onFormSubmit),
      onReset,
      setValues,
      updateFieldValue,
      slotProps,
    }
  },
})

</script>

<template>
  <form
    novalidate
    v-bind="$attrs"
    @submit.prevent="submit"
  >
    <slot v-bind="slotProps" />
  </form>
</template>
